<template>
  <q-card flat bordered id="intercom-email-sharing">
    <q-card-section>
      <div class="row justify-between">
        <div class="text-h6">Email Notification Settings</div>
        <div>
          Get the latest market information sent to your email, with latest rents, concessions, and
          an Excel export attached. Scheduled emails are disabled between midnight and 7 AM Central
          as we are updating our data for the next day.
        </div>
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section>
      <div id="intercom-email-cadence" class="q-mb-md">
        <schedule-maker
          v-if="modelValue"
          :model-value="modelValue.cron_expression"
          @update:model-value="
            $emit('update:modelValue', { ...modelValue, cron_expression: $event })
          "
        />
      </div>
      <div id="intercom-email-select">
        <email-select
          v-if="modelValue?.cron_expression && modelValue.cron_expression !== ''"
          :emails="modelValue.emails"
          @update-emails="updateEmails"
          :can-externally-share="canExternallyShare"
        />
      </div>
      <div class="row">
        <q-banner
          v-if="billedPerUnit && invalidEmailList"
          class="q-mt-md fancy-feature-box active text-primary col-12"
          dense
          rounded
          inline-actions
        >
          <template #default>
            <div class="row items-center q-gutter-x-sm no-wrap">
              <q-icon name="info" size="md"></q-icon>
              <div>
                External sharing is disabled. External sharing is only available for properties in
                {{ organization?.display_name }}'s portfolio.
              </div>
            </div>
          </template>
        </q-banner>
        <q-banner
          v-else-if="billedPerSeat && invalidEmailList"
          class="q-mt-md fancy-feature-box active text-primary col-12"
          dense
          rounded
          inline-actions
        >
          <template #default>
            <div class="row items-center q-gutter-x-sm no-wrap">
              <q-icon name="info" size="md"></q-icon>
              <div>
                External sharing is disabled. To add to your subscription, please reach out.
              </div>
            </div>
          </template>
          <template #action>
            <q-btn flat no-caps @click="openIntercom"> Upgrade </q-btn>
          </template>
        </q-banner>
      </div>
      <div class="row q-mt-md">
        <q-banner
          v-if="hasNewOwner && !dismissedInfo"
          inline-actions
          dense
          class="bg-blue text-white"
        >
          Emails have been automatically updated based on owner transfer.
          <template #action>
            <q-btn dense flat @click="dismissedInfo = true">
              <q-icon size="xs" name="close"></q-icon>
            </q-btn>
          </template>
        </q-banner>
        <q-space />
        <q-btn flat color="primary" label="Save as Default" no-caps @click="saveAsDefault">
          <q-tooltip>
            Save current email recipients and schedule as default for new reports
          </q-tooltip>
        </q-btn>
      </div>
    </q-card-section>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import EmailSelect from '@/components/EmailSelect.vue'
import type { EmailScheduleAttributes } from 'common'
import { useUserStore } from '@/stores/user.ts'
import { useStripeStore } from '@/stores/stripe'
import ScheduleMaker from './ScheduleMaker.vue'

declare const Intercom: (action: string, ...args: any[]) => void

export default {
  name: 'EmailNotifications',
  components: { EmailSelect, ScheduleMaker },
  props: {
    modelValue: {
      type: Object as PropType<EmailScheduleAttributes>,
      default: () => ({
        emails: [],
        cron_expression: null
      })
    },
    canExternallyShare: {
      type: Boolean as PropType<boolean | null>,
      default: null
    },
    reportIsInPortfolio: {
      type: Boolean as PropType<boolean | null>,
      default: null
    },
    invalidEmailList: {
      type: Boolean,
      default: false
    },
    hasNewOwner: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  mounted() {
    if (this.modelValue?.id === -1) {
      // This is a new report, so we need to load the default settings.
      const defaults = localStorage.getItem('email_notification_defaults')
      if (defaults) {
        try {
          const parsed = JSON.parse(defaults)
          if (parsed.cron_expression !== null && parsed.cron_expression !== undefined) {
            this.$emit('update:modelValue', {
              ...this.modelValue,
              cron_expression: parsed.cron_expression,
              emails: parsed.cron_expression === '' ? [] : [this.user?.email]
            })
          }
          return
        } catch (e) {
          console.error('Failed to load email defaults', e)
        }
      }
    } else if (this.modelValue.id === undefined) {
      // The report is not new, but it has no email schedule, so use the
      // current user's email address as a default.
      this.$emit('update:modelValue', {
        ...this.modelValue,
        emails: [this.user?.email]
      })
    }
  },
  computed: {
    user() {
      return this.$auth0.user.value
    },
    billedPerSeat() {
      return useStripeStore().billedPerSeat
    },
    billedPerUnit() {
      return useStripeStore().billedPerUnit
    },
    organization() {
      return useUserStore().organization
    }
  },
  data() {
    return {
      dismissedInfo: false
    }
  },
  methods: {
    openIntercom() {
      this.$tracker.openedIntercom()
      Intercom('show')
    },
    updateEmails(emails: string[]) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        emails
      })
    },
    saveAsDefault() {
      const defaults = {
        cron_expression: this.modelValue?.cron_expression ?? ''
      }
      localStorage.setItem('email_notification_defaults', JSON.stringify(defaults))
      this.$q.notify({
        message: 'Email settings saved as default',
        color: 'positive',
        position: 'bottom'
      })
    }
  }
}
</script>
